import { pick } from 'lodash';
import { toWordsOrdinal } from 'number-to-words';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import {
    DateCalcType,
    SmartDatePeriodRelation,
    ServiceSelectionType,
    IService,
    IMinimalPaymentContent,
    TTimeUnitType,
    IRecurringSchedule,
    TCustomTimeUnitType,
    TRepetitionUnitType
} from '~/types/model.types';
import { DAY_NAMES, formatDate, MONTH_NAMES } from '~/utils/date.utils';
import { pluralize } from '~/utils/text.utils';
import { IMinimalPaymentVariableValue } from '~/types/server.types';
import { PaymentMethodType } from '~/types/pay.types';
import { IReceiptPaymentMethod } from '../../ClientExperience/OneFlow/FlowContent/ChargeBlock/ChargeBlockReceipt';
import { CXPaymentBlock } from '../../ClientExperience/OneFlow/models/cx-payment-block.model';
import { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { IInvoiceColumnHeaderBlockStyles } from '~/types/block-styles.types';
import { INITIAL_BLOCK_STYLES } from '~/utils/initial-block-styles';
import { getFixedT } from 'i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { getOrdinalNumber } from '~/packages/utils';

export const viewInvoicesContainerId = 'view-invoices-container';
export const allInvoicesModalId = 'all-invoices-modal';
export const paymentDetailsModalId = 'payment-details-modal';
export const invoiceModal = 'invoice-modal';

export const getPaymentMethodType = (
    paymentBlock: PaymentBlock | CXPaymentBlock
) => {
    if (paymentBlock.isManualPaid) {
        return 'Marked as paid';
    }
    const paymentMethodToString = {
        [PaymentMethodType.BankAccount]: 'Bank transfer',
        [PaymentMethodType.CreditCard]: 'Credit card'
    };
    const paymentMethod = paymentBlock.paymentMethodType;
    return paymentMethodToString[paymentMethod];
};

export const getPaymentMethodDetails = (
    paymentBlock: PaymentBlock | CXPaymentBlock
) => {
    if (paymentBlock.isManualPaid) {
        return 'Marked as paid';
    }
    if (
        !paymentBlock.isPending &&
        !paymentBlock.isPaid &&
        !paymentBlock.isRefunded
    ) {
        return '-';
    }
    return `${paymentBlock.paymentMethodName} (${paymentBlock.paymentMethodTypeName} ${paymentBlock.paymentMethodLast4})`;
};

export const smartDatePeriodRelationDisplayValue = (
    period: SmartDatePeriodRelation
) => {
    switch (period) {
        case SmartDatePeriodRelation.AfterProject:
            return 'after project';
        case SmartDatePeriodRelation.BeforeProject:
            return 'before project';
        case SmartDatePeriodRelation.AfterSent:
            return 'after invoice';
        default:
            return '';
    }
};

export const getSmartDateDisplayValue = (
    paymentContent: IMinimalPaymentContent
) => {
    const {
        smart_date_period,
        smart_date_period_relation,
        smart_date_period_type
    } = paymentContent;
    return `${smart_date_period} ${pluralize(
        smart_date_period_type,
        smart_date_period
    )} ${smartDatePeriodRelationDisplayValue(
        smart_date_period_relation
    )} (TBD)`;
};

interface IGetPaymentDateDisplayValue {
    date: string;
    paymentVariable: IMinimalPaymentVariableValue;
    paymentContent: IMinimalPaymentContent;
    isRecurring?: boolean;
    isTemplate?: boolean;
}

export const getPaymentDateDisplayValue = ({
    date,
    paymentVariable,
    paymentContent = {},
    isRecurring,
    isTemplate
}: IGetPaymentDateDisplayValue) => {
    const { date_calc_type } = paymentContent;
    const t = getFixedT(null, NAMESPACES.CLIENT_EXPERIENCE, 'payment_schedule');

    if (isRecurring && paymentVariable?.is_tbd && isTemplate) {
        return t('tbd');
    }

    // We have a definite date
    if (date && !paymentVariable?.is_tbd) {
        if (date_calc_type !== DateCalcType.Milestone) {
            return formatDate(date, 'MM D, YYYY');
        }
    }

    if (date_calc_type === DateCalcType.DateSent) {
        return formatDate(new Date(), 'MM D, YYYY');
    }

    // For public files on the CX side we want to show the date
    if (date_calc_type === DateCalcType.Today && !isTemplate) {
        return formatDate(new Date(), 'MM D, YYYY');
    }

    // If fixed date, use that date regardless
    if (date && date_calc_type === DateCalcType.Fixed) {
        return formatDate(date, 'MM D, YYYY');
    }

    // When in template mode, we want to return TBD text for
    // everything except for the cases already handled above
    switch (date_calc_type) {
        case DateCalcType.MidWay:
            return t('midway_tbd');
        case DateCalcType.Today:
            return t('today_tbd'); // For public files in the builder we want to show Today (TBD)
        case DateCalcType.ProjectDate:
            return t('project_date_tbd');
        case DateCalcType.Milestone:
            return `${paymentContent?.milestone_title || t('milestone_name')}`;
        case DateCalcType.SmartDate:
            return getSmartDateDisplayValue(paymentContent);
        default:
            return null;
    }
};

export const getDepositDate = ({
    isManualPaid,
    depositDate
}: {
    isManualPaid: boolean;
    depositDate: string;
}) => {
    if (isManualPaid) {
        return 'Marked as paid';
    }
    return depositDate ? formatDate(depositDate, 'MM D, YYYY') : '';
};

export function paymentBlockToReceiptPaymentMethod(
    paymentBlock: PaymentBlock | CXPaymentBlock
): IReceiptPaymentMethod {
    return {
        last4: paymentBlock?.paymentMethodLast4,
        typeName: paymentBlock?.paymentMethodTypeName
    };
}

export function getServiceSelectionTypeText(block: ServiceSelectionBlock) {
    if (!block) {
        return null;
    }
    return `${block.selection_required ? 'Required' : 'Optional'} ${
        block.selection_type === ServiceSelectionType.One
            ? 'single '
            : 'multiple '
    } selection between ${block.serviceItems.length} ${pluralize(
        'service',
        block.serviceItems.length
    )} ${block.isSessionSelection ? ' with scheduling ' : ''}`;
}

const INVOICE_OVERRIDABLE_PROPS: (keyof IService)[] = ['title', 'description'];

export function getServiceItemOverridableProps(serviceItem: Partial<IService>) {
    return pick(serviceItem, INVOICE_OVERRIDABLE_PROPS);
}

// Some invoice components can be rendered when there's no invoice block in the flow
// This function takes care of returning the correct fallbacks
export function getStylesAndTaxInfo(block: InvoiceBlock) {
    // styles
    let styles: IInvoiceColumnHeaderBlockStyles;

    const columnHeaderBlock = block?.invoiceColumnHeaderBlock;

    if (columnHeaderBlock) {
        styles = columnHeaderBlock.styles;
    } else {
        styles = INITIAL_BLOCK_STYLES.invoice_column_header_block(block);
    }

    // tax labels
    const taxes = {
        tax_1: { label: 'Tax 1', active: false },
        tax_2: { label: 'Tax 2', active: false },
        tax_3: { label: 'Tax 3', active: false }
    };
    const summaryBlock = block?.invoiceSummaryBlock;

    if (summaryBlock) {
        taxes.tax_1.label = summaryBlock.invoice_summary?.tax_1?.label;
        taxes.tax_2.label = summaryBlock.invoice_summary?.tax_2?.label;
        taxes.tax_3.label = summaryBlock.invoice_summary?.tax_3?.label;
        taxes.tax_1.active = summaryBlock.invoice_summary?.tax_1?.active;
        taxes.tax_2.active = summaryBlock.invoice_summary?.tax_2?.active;
        taxes.tax_3.active = summaryBlock.invoice_summary?.tax_3?.active;
    }

    return {
        styles,
        taxes
    };
}

// get types of invoice for mobile analytics
export function getInvoiceTypeForAnalytics(block: InvoiceBlock) {
    if (block.invoice_type === 'payment_schedule') {
        return block.hasServicesSelectionBlocks ? 'basic' : 'service_selection';
    }
    return block.hasServicesSelectionBlocks
        ? 'recurring'
        : 'recurring_service_selection';
}

export function getRecurringPropertiesToUpdateByStartDate({
    newStartDate,
    currentEndDate
}: {
    newStartDate: Date;
    currentEndDate: string;
}) {
    const { day_of_month, day_of_week, month_of_year, first_invoice_due_date } =
        getRecurringDetailsByDate(newStartDate);
    const end_date = getRecurringEndDate({
        recurringSchedule: {
            first_invoice_due_date,
            end_date: currentEndDate
        }
    });
    return {
        day_of_month,
        day_of_week,
        first_invoice_due_date,
        month_of_year,
        end_date
    };
}

export function getRecurringEndDate({
    recurringSchedule,
    upcomingDueDate = new Date()
}) {
    const { first_invoice_due_date, end_date } = recurringSchedule;
    const today = formatDate(new Date(), 'MM D, YY');
    const startDate = formatDate(first_invoice_due_date, 'MM D, YY');
    const endDate = formatDate(end_date, 'MM D, YY');
    const upcomingDate = formatDate(upcomingDueDate, 'MM D, YY');
    const maxTimestamp = Math.max(
        new Date(today).getTime(),
        new Date(startDate).getTime(),
        new Date(endDate).getTime(),
        new Date(upcomingDate).getTime()
    );
    const latestEndDate = formatDate(new Date(maxTimestamp), 'YYYY-MM-dd');
    return latestEndDate;
}

export function getRecurringSummarySuffix({
    recurringSchedule,
    unitType
}: {
    recurringSchedule: IRecurringSchedule;
    unitType: TRepetitionUnitType | TCustomTimeUnitType;
}) {
    if (!recurringSchedule) {
        return null;
    }
    const {
        day_of_week,
        day_of_month,
        month_of_year,
        custom_unit_count,
        custom_unit_type
    } = recurringSchedule;
    const ordinal_day_of_month = getOrdinalNumber(day_of_month);
    const typeMap: Record<string, TTimeUnitType> = {
        days: 'daily',
        weeks: 'weekly',
        months: 'monthly',
        years: 'yearly'
    };
    switch (unitType) {
        // handle regular unit types (daily, weekly etc.)
        case 'daily':
            return `everyday`;
        case 'weekly':
            return `every ${day_of_week}`;
        case 'monthly':
            return `on the ${ordinal_day_of_month} of every month`;
        case 'yearly':
            return `every year on ${day_of_month} ${month_of_year}`;
        // handle custom types
        case 'days':
            return `every ${custom_unit_count} days`;
        case 'weeks':
            return `on ${day_of_week}, every ${custom_unit_count} weeks`;
        case 'months':
            return `on the ${ordinal_day_of_month} day of every ${toWordsOrdinal(
                custom_unit_count
            )} month`;
        case 'custom':
            return getRecurringSummarySuffix({
                recurringSchedule,
                unitType:
                    custom_unit_count > 1
                        ? custom_unit_type
                        : typeMap[custom_unit_type]
            });
        default:
            return '';
    }
}
export function getRecurringDetailsByDate(date): Partial<IRecurringSchedule> {
    const first_invoice_due_date = formatDate(date, 'YYYY-MM-dd');
    const day_of_month = date.getDate();
    const day_of_week = DAY_NAMES[date.getDay()];
    const month_of_year = MONTH_NAMES[date.getMonth()];
    return { day_of_month, day_of_week, month_of_year, first_invoice_due_date };
}

export function formatFirstPaymentDueDate(date: Date) {
    return formatDate(date, 'YYYY-MM-dd');
}
